import { CommonModule } from '@angular/common';
import { Component } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { Router } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { FuseTranslationLoaderService } from '../../../../@fuse/services/translation-loader.service';
import { AppRoutes } from '../../const/AppRoutes';
import { IconsService } from '../../services/icons.service';
import {
  UpgradeFeatureKey,
  UpgradePanelService,
} from '../../services/upgrade-panel.service';
import { locale as english } from './i18n/en-CA';
import { locale as french } from './i18n/fr-CA';

interface UpgradeFeatureItem {
  key: string;
  soon?: boolean;
}

/**
 * Sections « vendeuses » d'un panneau : accroche (TAGLINE), bénéfices (BENEFITS.<key>.TITLE/TEXT),
 * étapes (STEPS.<key>) et phrase de clôture (PUNCH). Clés i18n sous UPGRADE_PANEL.<FEATURE>.
 * Un tableau vide masque la section.
 */
interface UpgradeFeatureContent {
  tagline: boolean;
  benefits: string[];
  steps: string[];
  punch: boolean;
}

/** Page « En savoir plus » de chaque panneau dans le Centre d'aide (docs/kb/pages côté API). */
const FEATURE_KB_SLUG: Record<UpgradeFeatureKey, string | null> = {
  features: 'fonctionnalites-pro',
  submissions: 'soumission-web-pro',
  automations: 'automatisations-pro',
  integrations: 'integrations-pro',
  ai: null,
};

const FEATURE_CONTENT: Record<UpgradeFeatureKey, UpgradeFeatureContent> = {
  // « Fonctionnalités [PRO] » : vue d'ensemble du forfait (regroupe les trois modules + KPI + CRM à venir).
  features: {
    tagline: true,
    benefits: ['SUBMISSIONS', 'AUTOMATIONS', 'INTEGRATIONS', 'KPI', 'CRM'],
    steps: ['S1', 'S2', 'S3'],
    punch: true,
  },
  submissions: {
    tagline: true,
    benefits: [
      'FORM',
      'NO_RETYPING',
      'ONE_CLICK',
      'INSTANT_REPLY',
      'NOTHING_LOST',
    ],
    steps: ['S1', 'S2', 'S3'],
    punch: true,
  },
  automations: {
    tagline: true,
    benefits: [
      'SCHEDULED_REPORTS',
      'RIGHT_PERIOD',
      'EMAIL_OR_DRIVE',
      'EXTERNAL_API',
      'IN_CONTROL',
      'WEBHOOKS_MCP',
    ],
    steps: ['S1', 'S2', 'S3'],
    punch: true,
  },
  integrations: {
    tagline: true,
    benefits: ['ZAPIER', 'QBO', 'GOOGLE', 'MICROSOFT', 'SECURE', 'SOON'],
    steps: ['S1', 'S2', 'S3'],
    punch: true,
  },
  ai: { tagline: false, benefits: [], steps: [], punch: false },
};

/**
 * Catalogue des arguments par fonctionnalité [PRO]. Les libellés vivent dans
 * i18n sous UPGRADE_PANEL.<FEATURE>.ITEMS.<key> ; `soon` = « à venir ».
 * Contenu marketing minimal pour l'instant (« Contenu à venir » quand vide).
 */
const FEATURE_ITEMS: Record<UpgradeFeatureKey, UpgradeFeatureItem[]> = {
  features: [],
  submissions: [],
  automations: [], // contenu vendeur complet dans FEATURE_CONTENT (bénéfices + étapes)
  integrations: [], // contenu vendeur complet dans FEATURE_CONTENT (bénéfices + étapes)
  // IA : à venir en V2 du paywall — aucun argument pour l'instant (le panneau affiche « Contenu à venir »).
  ai: [],
};

/**
 * Panneau latéral générique « Mise à niveau [PRO] » (paywall) : promotion d'une
 * fonctionnalité réservée aux forfaits PRO / Entreprise + bouton « Commander »
 * qui mène à la page « Demander une mise à niveau ».
 */
@Component({
  selector: 'app-upgrade-panel',
  templateUrl: './upgrade-panel.component.html',
  styleUrls: ['./upgrade-panel.component.scss'],
  standalone: true,
  imports: [CommonModule, TranslateModule, MatButtonModule, MatIconModule],
})
export class UpgradePanelComponent {
  feature: UpgradeFeatureKey = 'submissions';
  featureKey: string = 'SUBMISSIONS';
  items: UpgradeFeatureItem[] = [];
  content: UpgradeFeatureContent = FEATURE_CONTENT.submissions;
  kbSlug: string | null = null;
  upgradeIcon: string = IconsService.UPGRADE;

  constructor(
    private panelService: UpgradePanelService,
    private translationLoader: FuseTranslationLoaderService,
    private router: Router,
  ) {
    this.translationLoader.loadTranslations(english, french);
    const data = this.panelService.getData();
    this.feature = data?.feature ?? 'submissions';
    this.featureKey = this.feature.toUpperCase();
    this.items = FEATURE_ITEMS[this.feature] ?? [];
    this.content = FEATURE_CONTENT[this.feature] ?? FEATURE_CONTENT.submissions;
    this.kbSlug = FEATURE_KB_SLUG[this.feature] ?? null;
  }

  close(): void {
    this.panelService.emitResult(false);
  }

  /** « En savoir plus » : ferme le panneau et ouvre la page du Centre d'aide de cette fonctionnalité. */
  learnMore(): void {
    if (!this.kbSlug) {
      return;
    }
    const prefix = localStorage.getItem('PREFIX') || '';
    this.panelService.emitResult(false);
    this.router.navigate(
      ['/', prefix, AppRoutes.KB, this.kbSlug].filter(Boolean) as string[],
    );
  }

  /** « Commander » : ferme le panneau et ouvre la page « Demander une mise à niveau » (même cible que le sous-menu Commander). */
  order(): void {
    const prefix = localStorage.getItem('PREFIX') || '';
    this.panelService.emitResult(true);
    // navigateByUrl : la route contient une barre oblique (mise-a-niveau/demande) ;
    // router.navigate([...]) l'encoderait en un seul segment (%2F) et ne matcherait pas.
    this.router.navigateByUrl(
      '/' + [prefix, AppRoutes.UPGRADE_REQUEST].filter(Boolean).join('/'),
    );
  }
}
